import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { ImageListDialogComponent } from 'src/app/shared/dialogs';
import { IBusinessTripParticipantDocumentsDto } from 'src/app/shared/models/business-trip-participants.model';
import { ITravelRequest, ITravelRequestPartial } from 'src/app/shared/models/travel-request.model';
import { AlertService, AlertType, AuthService } from 'src/app/shared/services';
import { TravelRequestService } from 'src/app/shared/services/travel-request.service';

@Component({
  selector: 'app-upload-travel-documents-dialog',
  templateUrl: './upload-travel-documents-dialog.component.html',
  styleUrls: ['./upload-travel-documents-dialog.component.scss'],
  standalone: false,
})
export class UploadTravelDocumentsDialogComponent implements OnInit, OnDestroy {
  dialogRef = inject<MatDialogRef<UploadTravelDocumentsDialogComponent>>(MatDialogRef);
  data = inject<ITravelRequestPartial>(MAT_DIALOG_DATA);
  private travelRequestsService = inject(TravelRequestService);
  private authService = inject(AuthService);
  private dialog = inject(MatDialog);
  private alertService = inject(AlertService);
  translate = inject(TranslateService);

  uploadForm: FormGroup;
  private readonly userId = this.authService.userId;
  private destroy$ = new Subject<void>();
  isLoading: boolean = true;

  ngOnInit() {
    this.buildForm();
    this.getRequestDetails();
  }

  buildForm() {
    this.uploadForm = new FormGroup({
      boardingCards: new FormControl([]),
      additionalDocuments: new FormControl([]),
    });
  }

  getRequestDetails() {
    this.travelRequestsService
      .getTravelRequestById(this.userId(), this.data.businessTripId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: ITravelRequest) => {
        let participant: IBusinessTripParticipantDocumentsDto =
          data.businessTripParticipantDocumentsDto.find(
            (dto) => dto.participantId === this.userId(),
          );
        this.isLoading = false;
        this.uploadForm.controls.boardingCards.setValue(participant.boardingCards);
        this.uploadForm.controls.additionalDocuments.setValue(participant.additionalDocuments);
      });
  }

  uploadFile(event, controlName) {
    if (controlName === 'boardingCards') {
      this.uploadForm.controls.boardingCards.setValue([
        ...this.uploadForm.controls.boardingCards.value,
        ...event.target.files,
      ]);
    } else {
      this.uploadForm.controls.additionalDocuments.setValue([
        ...this.uploadForm.controls.additionalDocuments.value,
        ...event.target.files,
      ]);
    }
  }

  openUploadedFiles(controlName) {
    const files = this.uploadForm.get(`${controlName}`).value;
    const fileList = [];
    const fileContent = [];
    files.forEach((file) => {
      if (typeof file != 'string') {
        fileList.push(file.name);
        fileContent.push(file);
      } else {
        fileList.push(file);
      }
    });
    const dialogRef = this.dialog.open(ImageListDialogComponent, {
      data: {
        requestId: null,
        fileList,
        readOnly: false,
        fileContent,
        url: `api/business-trip/${this.authService.userId()}/${
          this.data.businessTripId
        }/${this.authService.userId()}/`,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.travelRequestsService
          .deleteFile(this.userId(), this.data.businessTripId, this.userId(), result)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {});
      }
      const filesLeftAfterDeletion = files.filter((file) =>
        file.name ? file.name !== result : file !== result,
      );
      this.uploadForm.get(`${controlName}`).setValue([...filesLeftAfterDeletion]);
    });
  }

  submitForm() {
    this.isLoading = true;
    const formData: FormData = new FormData();
    const payload: any = this.uploadForm.value;
    for (let file of payload.boardingCards) {
      if (typeof file != 'string') {
        formData.append(
          `boardingCards`,
          new Blob([file], {
            type: 'application/octet-stream',
          }),
          file.name,
        );
      }
    }

    for (let file of payload.additionalDocuments) {
      if (typeof file != 'string') {
        formData.append(
          `additionalDocuments`,
          new Blob([file], {
            type: 'application/octet-stream',
          }),
          file.name,
        );
      }
    }
    this.travelRequestsService
      .uploadtravelRequestDocuments(this.data.businessTripId, this.userId(), formData)
      .subscribe({
        next: () => {
          this.isLoading = false;
        },
        error: (error: HttpErrorResponse) => {
          this.isLoading = false;
          this.alertService.showAlert(error.statusText, AlertType.error);
        },
        complete: () => {
          this.dialogRef.close();
          this.alertService.showAlert(
            this.translate.instant('USER.TRAVEL_REQUESTS.SUCCESSFUL_SUBMIT'),
            AlertType.success,
          );
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
